// ─── Utility Types ────────────────────────────────────────────────────────────

/**
 * CSS style properties object as stored in the embed configuration.
 *
 * Intentionally kept loose due to legacy usage in Make.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CSSProperties = Record<string, any>;

// ─── Common Primitives ────────────────────────────────────────────────────────

export type EmbedConfigTheme = 'light' | 'dark';

export type CaptionsMediaSourceType = 'audio' | 'video' | 'text';

export type SoundwaveType =
  | 'none'
  | 'linearDots'
  | 'blob'
  | 'pulse'
  | 'burst'
  | 'waveBars'
  | 'wave'
  | 'roundBars'
  | 'line'
  | 'curve'
  | 'bricks'
  | 'pixel'
  | 'equalizer'
  | 'wideRoundBars';

export type SoundWaveGeneration = 'amplitudeBased' | 'accurate';

export type TrackType = 'text' | 'media' | 'audio' | 'waveform';

export enum ProgressType {
  BAR = 'twoColorBar',
  PLAYER = 'player',
}

export type Position<T = string | number, L = T> = {
  left: L;
  top: T;
};

export type Size<H = string | number, W = H> = {
  height: H;
  width: W;
};

export enum ImageOriginId {
  CANVA = 'canva',
}

export type CaptionsAnimationName =
  | 'activeLineScroll'
  | 'activeWordColor'
  | 'activeWordHighlight'
  | 'activeWordUnderline'
  | 'jump'
  | 'pop'
  | 'terminal'
  | 'typewriter';

export type AnimationProperties = {
  /**
   * color of the active line. expects a color in rgb/a format
   */
  lineActiveColor?: string;
  /**
   * color of future lines. expects a color in rgb/a format
   */
  lineFutureColor?: string;
  /**
   * color of the active word. expects a color in rgb/a format
   */
  wordActiveColor?: string;
  /**
   * special color that applies to the active word’s emphasis element. used for
   * activeWordHighlight, activeWordUnderline.expects a color in rgb/a format
   */
  wordEmphasisColor?: string;
  /**
   * color of future words. expects a color in rgb/a format
   */
  wordFutureColor?: string;
  /**
   * color of past words. expects a color in rgb/a format
   */
  wordPastColor?: string;
};

export type CaptionsAnimationConfig = {
  animationName: CaptionsAnimationName;
  enabled: boolean;
  properties: AnimationProperties;
};

export type GooeyTextConfig = {
  color: string;
  minimumPaddingCoef?: number;
  radius: number;
};

// ─── Inlined from image-search-service ────────────────────────────────────────

export type ImageMetadata = {
  attributionHtml?: {
    default: string;
  };
  attributionText: {
    /**
     * default attribution text
     */
    default: string;
  };
  /**
   * author/photographer (nullable)
   */
  author?: string;
  /**
   * url of the page where image is sourced from
   */
  contextUrl: string;
  description?: string;
  /**
   * domain of the source of the image
   */
  domain: string;
  purchaseUrl?: string;
  usageTerm?: string;
};

// ─── Inlined from utils/embed/video ───────────────────────────────────────────

export enum VideoScaling {
  FILL = 'FILL',
  FIT = 'FIT',
}

// ─── Inlined from blocks/TextOverlayModal/v2 ──────────────────────────────────

export type OverlayScaler = {
  fontSizeVw: number;
  fontWidthPercPerPx: number;
};

export type TextEditorConfig = {
  scaler?: OverlayScaler;
  styleContext?: {
    fontSize?: number;
    viewport?: Viewport;
  };
  /**
   * identifier for template if template was used to create the text box
   */
  templateId?: string;
  textStyle?: CSSProperties;
};

export type TextBuilderStyles = {
  emptyLineStyle: CSSProperties;
  lineStyle: CSSProperties;
  paragraphStyle: CSSProperties;
};

// ─── Inlined from utils/text-templates/types ──────────────────────────────────

export type AdvancedTextConfigs = {
  gooeyText?: GooeyTextConfig;
};

// ─── Inlined from redux/modules/embed/types ───────────────────────────────────

type EmbedStaticCrop = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

export type EmbedDynamicCrop = {
  /**
   * id (uuid) of the crop used
   */
  cropId: string;
  revisionId: string;
};

export type CropInfo = {
  dynamicCrop?: EmbedDynamicCrop;
  staticCrop?: EmbedStaticCrop;
};

// ─── Semantic Asset Tags ───────────────────────────────────────────────────────

export type KeyImageType = 'mainImage';
export type KeyTextType = 'podcastTitle' | 'episodeTitle' | 'mainText';
export type KeyAssetType = KeyImageType | KeyTextType;

// ─── Shared Structural Types ───────────────────────────────────────────────────

export type Layerable = {
  /**
   * identifier to specify how to layer the asset. The lower the layer identifier,
   * the higher the asset will be placed. (i.e., layerId of 0 will be placed in the
   * most foreground layer.
   */
  layerId: number;
};

/**
 * The embed config uses two different position conventions:
 *   - simple `{ left, top }` (see `Position<T>`)
 *   - this richer form with a `type` discriminant and a `properties` sub-object
 *
 * This type represents the latter, used for watermarks, soundwaves, and video clips.
 */
export type EmbedPosition = {
  left?: string;
  properties?: {
    /**
     * accepts any valid css value (i.e vh, vw, px)
     */
    top: number | string;
    /**
     * accepts any valid css value (i.e vh, vw, px)
     */
    left: number | string;
  };
  top?: string;
  type?: 'absolute';
};

export type Viewport = {
  height: number;
  width: number;
};

// ─── Transition Types ──────────────────────────────────────────────────────────

type Transition = {
  effect: string;
  durationMilli: number;
  to?: string;
};

// ─── Slideshow ────────────────────────────────────────────────────────────────

type EntryTransitionOptions = {
  /**
   * this field applies for wipe and slide effect
   */
  to?: ['left', 'right', 'top', 'bottom'];
};

type EntryTransitionItem = {
  /**
   * Fade is what some people call dissolve.
   */
  effect: 'fade' | 'wipe' | 'fadeIn' | 'cut' | 'slide';
  options?: EntryTransitionOptions;
};

export type EmbeddedText = {
  text: string;
  /**
   * determines the type of the text. for certain text type, it will behave differently
   * for certain image effects (i.e. replace podcast and episode title placeholders)
   */
  textType?: KeyTextType;
};

export type ImageEffectStyleAdjustments = {
  selector: string;
  styleKey: string;
  styleValue: string;
};

export type ImageEffectOptions = {
  /**
   * map of possible embedded text placeholder and replacement value info
   */
  embeddedTexts?: EmbeddedText[];
  /**
   * only used for kenburns
   */
  direction?: 'in' | 'out';
  /**
   * map of style key and value with the css selector the user of this image effect
   * is required to load these styles into the page before rendering the image effect
   */
  styleAdjustments?: ImageEffectStyleAdjustments[];
  /**
   * currently only supports pan. random will choose a random X, Y point and pan
   * to -X, -Y. X and Y are randomly but consistently generated (same point between
   * refreshes). If a direction is chosen, the image will be aligned by the center
   * of the axis that it’s not traveling along. (e.g 'right’: left-most X will be
   * chosen and will travel to the right-most X. Y will be set at 0). Absence of
   * this field will be assumed to be random.
   */
  to?: 'random' | 'right' | 'left' | 'top' | 'bottom';
};

export type ImageEffect = {
  /**
   * ken burns is same as zoom. certain effects utilize lottie web tool to apply
   * the effect and may require positioning and sizing to place the image at a
   * desired location.
   */
  effect:
    | 'clickbait'
    | 'fanfare'
    | 'fiveStars'
    | 'geo'
    | 'kenburns'
    | 'newEpisodeSoundOn'
    | 'pan'
    | 'paperRip'
    | 'pingPong'
    | 'none'
    | 'rotatingCube'
    | 'screenshot'
    | 'tweetExperience';
  effectType?: 'lottie' | 'default';
  options?: ImageEffectOptions;
};

type ImageAttribution = {
  style?: CSSProperties;
  /**
   * attribution text
   */
  text: string;
};

type BaseSourceImageOrigin = {
  origin: ImageOriginId;
};

export type CanvaImageOrigin = BaseSourceImageOrigin & {
  canvaDesignId: string;
  origin: ImageOriginId.CANVA;
};

export type SourceImageOrigin = CanvaImageOrigin;

export type SourceImage = SourceImageOrigin & {
  /**
   * identifier given by canva if image is generated using canva editor
   */
  canvaDesignId?: string;
  /**
   * operations required to apply to the source image to get to the image url
   */
  operations: [
    {
      properties: {
        bottom: number;
        left: number;
        right: number;
        top: number;
      };
      operationType: 'crop';
    },
  ];
  /**
   * origin/tool from which the image came from
   */
  origin?: string;
  /**
   * url of the source (original) image
   */
  url: string;
};

export type SlideshowItem = Layerable & {
  attributions: ImageAttribution[];
  editor: {
    cropper: {
      canvas: {
        height: number;
        left: number;
        top: number;
        width: number;
      };
      constrained: boolean;
      container: {
        height: number;
        width: number;
      };
    };
  };
  /**
   * ms since start of recording when this image should disappear. if not specified,
   * the start of the next slide will determine the end time of the current slide
   */
  endMilli?: number;
  entryTransition: string | EntryTransitionItem;
  imageEffect: ImageEffect;
  imageMetadata?: ImageMetadata;
  imageType?: KeyImageType;
  imageUrl: string;
  /**
   * this is a backfill slide
   */
  isBackfill: boolean;
  /**
   * associated keyword for this slide
   */
  keyword?: string;
  position?: Position<number>;
  sourceImage?: SourceImage;
  /**
   * ms since start of recording when this image should show up
   */
  startMilli: number;
  style?: CSSProperties;
};

// ─── Text Overlay ─────────────────────────────────────────────────────────────

type TextPositionProperties = {
  left?: string;
  horizontalAlign?: ['left', 'center', 'right'];
  top?: string;
  verticalAlign?: ['top', 'center', 'bottom'];
};

type TextPosition = {
  properties?: TextPositionProperties;
  type: ['align', 'absolute'];
};

type TextTransitionOptions = {
  /**
   * sets the z direction of the effect when applicable. only effects zoom right now
   */
  direction?: ['in', 'out'];
  /**
   * sets the x/y direction of the effect when applicable
   */
  to?: ['right', 'left', 'top', 'bottom'];
};

type TextTransition = {
  /**
   * duration of animation in ms
   */
  delay: number;
  duration: number;
  /**
   * netlifyAnimation* effects are taken from netlify
   */
  effect: ['fade', 'wipe', 'slide', 'zoom', 'cut'];
  options?: TextTransitionOptions;
  /**
   * deprecated. use syncMode field
   * @deprecated
   */
  sync: boolean;
  /**
   * specify how the animation should behave. if all is selected, all assets are
   * animated at the same time. the animation will be synchronized based on the asset selected
   */
  syncMode?: 'all' | 'paragraph' | 'line' | 'word' | 'letter';
  target: string;
};

type TextTransitions = {
  in: TextTransition;
  out: TextTransition;
};

type ContainerDecorator = {
  /**
   * name of the decorator to use
   */
  name: string;
};

type TextProperties = {
  overflow?: {
    /**
     * any valid value for overflow css rule
     */
    value?: string;
    target: ['paragraph'];
  };
};

export type TextOverlayItem = Layerable & {
  advancedTextConfigs?: AdvancedTextConfigs;
  containerDecorator: ContainerDecorator;
  containerStyle: CSSProperties;
  /**
   * not implemented yet
   */
  effect?: string;
  editor?: TextEditorConfig;
  endMilli: number;
  position: TextPosition;
  properties?: TextProperties;
  startMilli: number;
  /**
   * exactly one of text or textHtml is expected to be used. if textHtml is used,
   * whatever value is set is used in the video output and preview. if text is used,
   * we wrap it in our own html
   */
  text: string;
  textBuilderStyles?: TextBuilderStyles;
  /**
   * exactly one of text or textHtml is expected to be used. if textHtml is used,
   * whatever value is set is used in the video output and preview. if text is used,
   * we wrap it in our own html
   */
  textHtml?: string;
  textStyle?: CSSProperties;
  textType?: KeyTextType;
  transitions: TextTransitions;
  version?: number;
};

// ─── Audio ────────────────────────────────────────────────────────────────────

type AudioInfoTransition = Record<'in' | 'out', Transition>;

export type AudioInfo = Layerable & {
  /**
   * duration of the audio to be played (i.e. the duration after trimming)
   */
  durationMilli: number;
  editor: {
    track: {
      type: 'audio';
      index: number;
    };
  };
  /**
   * language of the audio. stored and used by frontend
   */
  language?: string;
  /**
   * id (uuid) of the media segment info to be used with this audio
   */
  mediaSegmentInfoId?: string;
  /**
   * local start time where 0 is the beginning of the recording. this time is in
   * relation to the recording
   */
  playOffsetMilli: number;
  /**
   * recordingId for the source audio that appears in the timeline
   */
  recordingId: number;
  /**
   * global start time where 0 is the beginning of the video. this time is in
   * relation to the entire timeline.
   */
  startAtMilli: number;
  transition?: AudioInfoTransition;
};

// ─── Soundwave ────────────────────────────────────────────────────────────────

type SoundwaveStyle = {
  /**
   * used for brick or equalizer patterns only accepts any valid css value (i.e vh, vw, px)
   */
  brickGapY?: string;
  /**
   * color does not get applied if a pattern or style with a predefined color is
   * selected. (i.e. equalizer, rings with rainbowRingColor, rings with randomRingColor)
   */
  color: string;
  /**
   * accepts any valid css value (i.e vh, vw, px)
   */
  height: string;
  /**
   * accepts any valid css value (i.e vh, vw, px)
   */
  lineWidth: string;
  /**
   * name of the pattern as used from audiogram
   */
  pattern: SoundwaveType;
  /**
   * used for rings pattern only. multiplies the radius of the rings by this amount
   */
  radiusMultiplier?: number;
  /**
   * used for rings pattern only. applies random color to the rings
   */
  randomRingColor?: boolean;
  rainbowRingColor?: boolean;
  /**
   * color does not get applied if a pattern or style with a predefined color is
   * selected. some waveform types can take a secondary color for additional styling (i.e. blob)
   */
  secondaryColor?: string;
  /**
   * accepts any valid css value (i.e vh, vw, px)
   */
  width: string;
};

export type Soundwave = Layerable & {
  enabled: boolean;
  /**
   * if not provided, assumed to be the end of the video
   */
  endMilli?: number;
  framesPerSecond: number;
  position?: EmbedPosition;
  /**
   * if not provided, assumed to be 0
   */
  /**
   * waveform type used to generate the soundwave. this is stored and used by the frontend
   */
  preferredWaveformType?: SoundWaveGeneration;
  startMilli?: number;
  style?: SoundwaveStyle;
  waveformPrefId?: number;
};

// ─── Watermark ────────────────────────────────────────────────────────────────

type WatermarkStyle = {
  height: number | string;
  width: number | string;
};

export type Watermark = {
  /**
   * if not specified, will default to end of the entire presentation
   */
  endMilli?: number;
  imageType?: KeyImageType;
  position?: EmbedPosition;
  /**
   * if not specified, will default to the beginnig of the presentation
   */
  startMilli?: number;
  style: WatermarkStyle;
  /**
   * url to the watermark image
   */
  url: string;
};

// ─── Captions ────────────────────────────────────────────────────────────────

export type CaptionSource = {
  /**
   * cuid of eddy project used as caption source
   */
  eddyProjectCuid?: string;
  /**
   * if not provided, the latest revision of the transcript id will be used
   */
  revisionId?: string;
  /**
   * id (uuid) of the transcript used with transcript service.
   */
  transcriptId?: string;
};

type StyleContext = {
  fontSize: number;
  textBoxHeight: number;
  viewport: Viewport;
};

type CaptionsEditor = {
  mediaSource: {
    id: string | number;
    type: CaptionsMediaSourceType;
  };
  shouldRechunkCaptionsOnEditorLoad?: boolean;
  styleContext?: StyleContext;
  templateId?: string;
  templateName?: string;
};

export type CaptionsRegionProperties = {
  /**
   * accepts any valid css value (i.e vh, vw, px) this field is required only for
   * absolute region type
   */
  left: string;
  /**
   * name is required for preset region types
   */
  name?: ['main'];
  /**
   * accepts any valid css value (i.e vh, vw, px) this field is required only for
   * absolute region type
   */
  top: string;
};

type CaptionsRegion = {
  properties?: CaptionsRegionProperties;
  type: 'preset' | 'absolute';
};

type FollowsAnimation = {
  containerStyle?: CSSProperties;
  enabled: boolean;
  textStyle?: CSSProperties;
};

type CaptionsAnimation = {
  follow?: FollowsAnimation;
};

export type Captions = {
  animation?: CaptionsAnimation;
  advancedAnimation?: CaptionsAnimationConfig;
  advancedTextConfigs?: AdvancedTextConfigs;
  captionSource?: CaptionSource;
  containerStyle?: CSSProperties;
  editor?: CaptionsEditor;
  enabled: boolean;
  region?: CaptionsRegion;
  textStyle?: CSSProperties;
};

// ─── Video Clips ──────────────────────────────────────────────────────────────

export type VideoLayoutTimelineInfo = {
  /**
   * id (uuid) of the layout timeline data
   */
  layoutTimelineId: string;
  revisionId: string;
};

export type VideoClip = Layerable & {
  /**
   * video asset type. if this video is sourced from a gif it is loopable. it
   * will start playing at startMilli and loop until endMilli. if not specified,
   * will default to video assetType
   */
  assetType?: 'gif' | 'video';
  audio?: {
    /**
     * if true, will mixdown the audio levels during video render. if false, will
     * ignore all volume mixdown during video export. this should be used if the
     * audio will be mixed down before hand.
     * */
    enableVideoRenderMixdown: boolean;
    audioUrl?: string;
    transition?: {
      in: Transition;
      out: Transition;
    };
    volume?: {
      /**
       * volume level of all other audio tracks, including the main audio track
       */
      other: number;
      /**
       * volume level of the video clip
       */
      self: number;
    };
    enabled: boolean;
  };
  backfill?: {
    /**
     * if set to true, uses blurred video/image as the background. It will use the
     * style field’s width and height field and fill the blurred video/image into that canvas.
     */
    enableBlur?: boolean;
    position: EmbedPosition;
    style: {
      /**
       * if enableBlur field is set, the background is not displayed, therefore,
       * this field is essentially ignored.
       */
      backgroundColor: string;
      height: string;
      width: string;
    };
  };
  cropInfo?: CropInfo;
  editor: {
    videoHeight: number;
    videoWidth: number;
    viewport: Viewport;
    zoom: number;
    scaling?: VideoScaling;
  };
  /**
   * ms since start of entire video when this video clip should disappear. if not
   * specified, the video clip will play until it finishes, or until the start of the
   * next video clip. Also, note that if the endMilli is set to something higher than
   * startMilli + duration of the video clip, this endMilli field will be ignored.
   */
  endMilli?: number;
  layoutTimelineInfo?: VideoLayoutTimelineInfo;
  /**
   * id (uuid) of the media segment info to be used with this audio
   */
  mediaSegmentInfoId?: string;
  /**
   * ms since the start of the original video clip. amount of time to seek in the
   * video clip. this is used for trim purposes. if this field is specified, the video
   * clip will start playing at the offset.
   */
  playOffsetMilli: number;
  position: EmbedPosition;
  previewThumbnail?: {
    url?: string;
  };
  /**
   * duration of the original video clip (before trimming) in ms
   */
  sourceDurationMilli: number;
  /**
   * ms since start of entire video when this video clip should start
   */
  startMilli: number;
  style: {
    height: string;
    width: string;
  };
  transition?: {
    in: Transition;
    out: Transition;
  };
  /**
   * the video id assigned by the media upload service. if specified, special
   * functionality can be enabled, such as utilizing mpeg dash. even though this
   * is not required, we may make this required in the future, so it’s highly
   * recommended to send the video id.
   */
  videoId: number;
  /**
   * url to the video. This should be a SpareMin CDN url
   */
  videoUrl: string;
  trimEndMillis?: number;
  trimStartMillis?: number;
};

// ─── Edge Videos ─────────────────────────────────────────────────────────────

export type EdgeVideosConfig = {
  /**
   * the video id assigned by the media upload service
   */
  videoId: number;
};

export type EdgeVideos = {
  intro?: EdgeVideosConfig;
  outro?: EdgeVideosConfig;
};

// ─── Progress / Timer ─────────────────────────────────────────────────────────

export type Progress = {
  /**
   * color of the elapsed portion of the progress ui
   */
  elapsedColor: string;
  enabled: boolean;
  position?: {
    properties: {
      bottom?: string | number;
      left?: string | number;
      right?: string | number;
      top?: string | number;
    };
    type: 'absolute';
  };
  style: {
    width: string | number;
    height: string | number;
  };
  /**
   * type of progress ui to draw
   */
  type?: ProgressType;
  /**
   * color of the unelapsed portion of the progress ui
   */
  unelapsedColor: string;
};

export type Timer = {
  enabled: boolean;
  /**
   * direction of timer (countUp/countdown)
   */
  countDirection?: 'countUp' | 'countDown';
  containerStyle?: CSSProperties;
  position: {
    properties: Position<string>;
    type: 'absolute';
  };
};

// ─── Miscellaneous ────────────────────────────────────────────────────────────

export type VersionInfo = {
  embedApp: string;
};

export type Dimensions = {
  width: number;
  height: number;
};

export type MediaContainer = {
  style: CSSProperties;
};

export type MediaSegmentInfo = {
  /**
   * id (uuid) of the segment data
   */
  id: string;
  revisionId: string;
};

export type LockSpecReference = {
  uuid: string;
};

// ─── Root Config Types ────────────────────────────────────────────────────────

export type EmbedConfig = {
  audioInfo?: AudioInfo[];
  bigPlay?: boolean;
  captions?: Captions;
  /**
   * the id of the user that created this embed config
   */
  creatorUserId?: number;
  dimensions?: Dimensions;
  edgeVideos?: EdgeVideos;
  /**
   * helps determine the total duration of the slideshow when the recording information
   * is missing
   */
  expectedDurationMilli?: number;
  fromProject?: {
    /**
     * uuid of the project as returned from the video project service
     */
    projectUuid: string;
  };
  hideAvatars?: boolean;
  /**
   * order of the layer in the editor
   */
  layerOrder?: TrackType[];
  /**
   * lock spec for the embed config
   */
  lockSpec?: LockSpecReference;
  mainMediaContainer?: MediaContainer;
  mediaSegmentInfo?: MediaSegmentInfo[];
  progress?: Progress;
  /**
   * if specified, the id is used to retrieve the recording url and owner/caller information
   */
  recordingId?: number;
  slideshowInfo?: SlideshowItem[];
  soundwave?: Soundwave;
  templateId?: number;
  textOverlayInfo?: TextOverlayItem[];
  theme?: EmbedConfigTheme;
  timer?: Timer[];
  versionInfo?: VersionInfo;
  videoClips?: VideoClip[];
  watermark?: Watermark[];
};

export type WidgetConfig = {
  embedConfig: EmbedConfig;
  wid: string;
};
